import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  Cell,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { heatmapHex, heatmapTextHex } from '../../../support/aiEraReport/ui/utils';
import {
  atLeastLevel,
  type DetailLevel,
} from '../../../support/aiEraReport/shared/detail-level';
import {
  TOPO_TIERS,
  TOPO_EDGE_KIND_META,
  type TopoModel,
  type TopoEdgeKind,
} from '../../../support/dataImport/topology/topologyTypes';
import type { ReportModel, ReportSectionConfig } from '../../../support/reportView/types';

/**
 * The reusable report-section library. Each `SectionDefinition` is a
 * self-contained, engine-agnostic renderer that reads one slice of the report
 * `ReportModel.data` (chosen by the instance's `dataKey`) and declares the
 * options an admin can set in the report builder. The registry maps a section
 * `type` string to its definition; `ReportRenderer` and `ReportBuilder` both
 * drive off it, so adding a section type here makes it available everywhere.
 */

// --- Theme ------------------------------------------------------------------

export type ReportTheme = {
  dark: boolean;
  /** MUI `sx.color` token (undefined = inherit default text). */
  ink?: string;
  inkMuted: string;
  surface?: string;
  grid: string;
  /** A concrete colour for SVG/Recharts (can't take an sx token). */
  axisTick: string;
  accent: string;
};

export function makeReportTheme(dark?: boolean): ReportTheme {
  return {
    dark: !!dark,
    ink: dark ? '#f1f5fb' : undefined,
    inkMuted: dark ? '#9fb3cc' : 'text.secondary',
    surface: dark ? 'rgba(16,31,56,0.7)' : undefined,
    grid: dark ? 'rgba(159,179,204,0.25)' : '#d0d7de',
    axisTick: dark ? '#9fb3cc' : '#57606a',
    accent: '#02c8ff',
  };
}

// The four severity band colours, matched to `ui/utils` DEFAULT_BAND_PALETTE.
const SEVERITY_COLOR: Record<string, string> = {
  Critical: '#C00000',
  High: '#FF0000',
  Medium: '#FFC000',
  Low: '#92D050',
};
const sevColor = (s?: string): string => (s && SEVERITY_COLOR[s]) || '#92D050';

const EDGE_COLOR: Record<TopoEdgeKind, string> = {
  wan: '#7c5cff',
  uplink: '#02c8ff',
  trunk: '#7fd4ff',
  mgmt: '#8a9bb5',
};

// --- Section definition contract --------------------------------------------

export type OptionFieldDef = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'select';
  /** For `select`. */
  choices?: Array<{ value: string; label: string }>;
  help?: string;
};

export type SectionRenderProps = {
  config: ReportSectionConfig;
  /** `model.data[dataKey ?? defaultDataKey]`. Loosely typed — the model is
   *  engine-agnostic, so each renderer narrows what it needs. */
  data: any;
  model: ReportModel;
  level: DetailLevel;
  theme: ReportTheme;
  roleView: string;
};

export type SectionDefinition = {
  type: string;
  /** Admin-facing name in the builder. */
  label: string;
  defaultTitle: string;
  defaultMinLevel: DetailLevel;
  defaultDataKey?: string;
  /** 'bare' sections render their own container (e.g. the score header). */
  chrome: 'card' | 'bare';
  /** Type-specific options exposed in the builder (title/hint/minLevel/dataKey
   *  are edited generically for every section). */
  optionsSchema?: OptionFieldDef[];
  /** Returns null to render nothing (empty data) — the renderer then omits the
   *  whole card so an absent slice never shows an empty box. */
  render: (props: SectionRenderProps) => React.ReactNode;
};

const opt = <T,>(config: ReportSectionConfig, key: string, fallback: T): T =>
  (config.options?.[key] as T) ?? fallback;

// --- Shared bits ------------------------------------------------------------

const Kpi: React.FC<{ label: string; value: string; hint?: string; theme: ReportTheme }> = ({
  label,
  value,
  hint,
  theme,
}) => (
  <Box>
    <Typography variant="overline" sx={{ color: theme.inkMuted, display: 'block', lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: theme.ink, fontWeight: 300 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: theme.inkMuted }}>
        {hint}
      </Typography>
    )}
  </Box>
);

// --- Section renderers ------------------------------------------------------

const scoreHeader: SectionDefinition = {
  type: 'scoreHeader',
  label: 'Score header',
  defaultTitle: 'Overall',
  defaultMinLevel: 'executive',
  defaultDataKey: 'overall',
  chrome: 'bare',
  render: ({ data, theme }) => {
    if (!data) return null;
    const { overline, score, scoreMax = 100, label, kpis = [] } = data as {
      overline?: string;
      score: number;
      scoreMax?: number;
      label?: string;
      kpis?: Array<{ label: string; value: string; hint?: string }>;
    };
    return (
      <>
        <Typography variant="overline" sx={{ color: theme.inkMuted }}>
          {overline}
        </Typography>
        <Box display="flex" alignItems="baseline" gap={1.5} mt={0.5}>
          <Typography variant="h3" sx={{ color: theme.ink, fontWeight: 300, lineHeight: 1 }}>
            {score}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.inkMuted }}>
            / {scoreMax}
          </Typography>
          {label && (
            <Chip size="small" label={label} sx={{ bgcolor: sevColor(label), color: '#fff', fontWeight: 700 }} />
          )}
        </Box>
        {kpis.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={{ xs: 3, md: 5 }} mt={2}>
            {kpis.map((k) => (
              <Kpi key={k.label} label={k.label} value={k.value} hint={k.hint} theme={theme} />
            ))}
          </Box>
        )}
      </>
    );
  },
};

const narrative: SectionDefinition = {
  type: 'narrative',
  label: 'Narrative',
  defaultTitle: 'Summary',
  defaultMinLevel: 'executive',
  chrome: 'card',
  render: ({ data, theme }) => {
    if (!data) return null;
    const { text, groups = [] } = data as {
      text?: string;
      groups?: Array<{ label: string; items: string[]; ordered?: boolean }>;
    };
    if (!text && groups.length === 0) return null;
    return (
      <>
        {text && (
          <Typography variant="body1" sx={{ color: theme.dark ? '#cdd9ea' : undefined }}>
            {text}
          </Typography>
        )}
        {groups.map((g) => (
          <Box key={g.label} mt={2}>
            <Typography variant="overline" sx={{ color: theme.inkMuted }}>
              {g.label}
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ mt: 0.5, pl: 2.5, m: 0 }}>
              {g.items.map((s, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ color: theme.ink }}>
                  {s}
                </Typography>
              ))}
            </Stack>
          </Box>
        ))}
      </>
    );
  },
};

const threatBars: SectionDefinition = {
  type: 'threatBars',
  label: 'Ranked bar chart',
  defaultTitle: 'Top exposure',
  defaultMinLevel: 'executive',
  defaultDataKey: 'topThreats',
  chrome: 'card',
  optionsSchema: [
    { key: 'limitExecutive', label: 'Max bars at Executive level', type: 'number' },
    { key: 'limit', label: 'Max bars (Business/Technical)', type: 'number' },
  ],
  render: ({ data, theme, level, config }) => {
    const items: Array<{ name: string; score: number; severity?: string; meta?: string }> =
      (data as any)?.items ?? [];
    if (items.length === 0) return null;
    const limit = level === 'executive' ? opt(config, 'limitExecutive', 5) : opt(config, 'limit', 8);
    const bars = items.slice(0, limit);
    return (
      <ResponsiveContainer width="100%" height={Math.max(200, bars.length * 40)}>
        <BarChart layout="vertical" data={bars} margin={{ top: 4, right: 24, bottom: 4, left: 8 }}>
          <XAxis type="number" domain={[0, 100]} tick={{ fill: theme.axisTick, fontSize: 11 }} />
          <YAxis type="category" dataKey="name" width={180} tick={{ fill: theme.axisTick, fontSize: 11 }} />
          <ReTooltip
            formatter={(value, _n, entry) => [
              `${value} / 100${entry?.payload?.severity ? ` · ${entry.payload.severity}` : ''}${
                entry?.payload?.meta ? ` (${entry.payload.meta})` : ''
              }`,
              'Score',
            ]}
            contentStyle={theme.dark ? { background: '#0b1830', border: `1px solid ${theme.grid}`, color: '#f1f5fb' } : undefined}
          />
          <Bar dataKey="score" radius={[0, 4, 4, 0]}>
            {bars.map((t) => (
              <Cell key={t.name} fill={t.severity ? sevColor(t.severity) : theme.accent} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  },
};

const radar: SectionDefinition = {
  type: 'radar',
  label: 'Radar chart',
  defaultTitle: 'By domain',
  defaultMinLevel: 'business',
  defaultDataKey: 'domainMaturity',
  chrome: 'card',
  render: ({ data, theme }) => {
    const axes: Array<{ axis: string; label?: string; current: number; target?: number }> =
      (data as any)?.axes ?? [];
    if (axes.length === 0) return null;
    const max = (data as any)?.max ?? 4;
    const hasTarget = axes.some((a) => typeof a.target === 'number');
    return (
      <ResponsiveContainer width="100%" height={320}>
        <RadarChart data={axes} outerRadius="72%">
          <PolarGrid stroke={theme.grid} />
          <PolarAngleAxis dataKey="axis" tick={{ fill: theme.axisTick, fontSize: 12 }} />
          <PolarRadiusAxis domain={[0, max]} tick={{ fill: theme.axisTick, fontSize: 10 }} />
          {hasTarget && (
            <Radar name="Target" dataKey="target" stroke="#9fb3cc" strokeDasharray="5 5" fill="transparent" />
          )}
          <Radar name="Current" dataKey="current" stroke={theme.accent} fill={theme.accent} fillOpacity={0.35} />
          <ReTooltip
            formatter={(value, name) => [value, name]}
            labelFormatter={(l) => axes.find((d) => d.axis === l)?.label || l}
            contentStyle={theme.dark ? { background: '#0b1830', border: `1px solid ${theme.grid}`, color: '#f1f5fb' } : undefined}
          />
        </RadarChart>
      </ResponsiveContainer>
    );
  },
};

const heatmap: SectionDefinition = {
  type: 'heatmap',
  label: 'Heatmap matrix',
  defaultTitle: 'Exposure matrix',
  defaultMinLevel: 'business',
  defaultDataKey: 'threatMatrix',
  chrome: 'card',
  render: ({ data, theme }) => {
    const columns: Array<{ id: string; label: string }> = (data as any)?.columns ?? [];
    const rows: Array<{ label: string; shortLabel?: string; cells: Array<number | null> }> =
      (data as any)?.rows ?? [];
    if (rows.length === 0) return null;
    return (
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderColor: theme.grid, bgcolor: 'transparent', color: theme.inkMuted }}>Row</TableCell>
              {columns.map((c) => (
                <TableCell key={c.id} align="center" sx={{ borderColor: theme.grid, bgcolor: 'transparent', color: theme.inkMuted, fontSize: 11 }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, ri) => (
              <TableRow key={ri}>
                <TableCell sx={{ borderColor: theme.grid, color: theme.inkMuted, fontSize: 12, maxWidth: 220 }}>
                  <Tooltip title={r.label}>
                    <span>{r.shortLabel ?? r.label}</span>
                  </Tooltip>
                </TableCell>
                {r.cells.map((cell, i) => (
                  <TableCell
                    key={i}
                    align="center"
                    sx={{
                      borderColor: theme.grid,
                      bgcolor: `#${heatmapHex(cell)}`,
                      color: `#${heatmapTextHex(cell)}`,
                      fontSize: 11,
                      fontWeight: 600,
                      px: 0.5,
                    }}
                  >
                    {cell === null ? '' : Math.round(cell * 100)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
};

type RecItem = {
  controlId?: string;
  rank?: number;
  title: string;
  subtitle?: string;
  scoreDrop?: number;
  body?: string;
  bodyByRole?: Record<string, string>;
  steps?: string[];
  tags?: string[];
};

const recommendations: SectionDefinition = {
  type: 'recommendations',
  label: 'Recommendation cards',
  defaultTitle: 'Recommendations',
  defaultMinLevel: 'executive',
  defaultDataKey: 'recommendations',
  chrome: 'card',
  optionsSchema: [
    { key: 'limitExecutive', label: 'Max cards at Executive level', type: 'number' },
    { key: 'stepsMinLevel', label: 'Show action steps from level', type: 'select', choices: [
      { value: 'executive', label: 'Executive' },
      { value: 'business', label: 'Business' },
      { value: 'technical', label: 'Technical' },
    ] },
    { key: 'hideWhenEmpty', label: 'Hide the section when there is nothing to show', type: 'boolean' },
    { key: 'emptyMessage', label: 'Message when nothing to recommend', type: 'text' },
  ],
  render: ({ data, theme, level, config, roleView }) => {
    const items: RecItem[] = (data as any)?.items ?? [];
    if (items.length === 0 && opt<boolean>(config, 'hideWhenEmpty', false)) return null;
    const emptyMessage = opt<string>(
      config,
      'emptyMessage',
      'Every assessed control is already at the target level — no remediation to prioritise.'
    );
    if (items.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: theme.inkMuted }}>
          {emptyMessage}
        </Typography>
      );
    }
    const stepsMin = opt<DetailLevel>(config, 'stepsMinLevel', 'technical');
    const showSteps = atLeastLevel(level, stepsMin);
    const shown = level === 'executive' ? items.slice(0, opt(config, 'limitExecutive', 3)) : items;
    return (
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
        {shown.map((r, i) => {
          const body = (r.bodyByRole && r.bodyByRole[roleView]) || r.body;
          return (
            <Paper
              key={r.controlId ?? i}
              variant="outlined"
              sx={{ p: 2, bgcolor: 'transparent', borderColor: theme.grid, borderLeft: `3px solid ${theme.accent}` }}
            >
              <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1}>
                <Typography variant="subtitle2" sx={{ color: theme.ink }}>
                  {r.rank ? `${r.rank}. ` : ''}
                  {r.title}
                </Typography>
                {typeof r.scoreDrop === 'number' && (
                  <Chip size="small" variant="outlined" label={`−${Math.round(r.scoreDrop)} pts`} sx={{ color: theme.inkMuted, borderColor: theme.grid }} />
                )}
              </Box>
              {r.subtitle && (
                <Typography variant="caption" sx={{ color: theme.inkMuted, display: 'block' }}>
                  {r.subtitle}
                </Typography>
              )}
              {body && (
                <Typography variant="body2" sx={{ color: theme.dark ? '#cdd9ea' : undefined, mt: 1 }}>
                  {body}
                </Typography>
              )}
              {showSteps && r.steps && r.steps.length > 0 && (
                <Box mt={1}>
                  <Typography variant="overline" sx={{ color: theme.inkMuted }}>
                    Action steps
                  </Typography>
                  <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
                    {r.steps.map((s, si) => (
                      <Typography key={si} component="li" variant="body2" sx={{ color: theme.inkMuted }}>
                        {s}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
              {r.tags && r.tags.length > 0 && (
                <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                  {r.tags.map((t) => (
                    <Chip key={t} size="small" label={t} sx={{ bgcolor: theme.dark ? 'rgba(2,200,255,0.1)' : 'rgba(0,0,0,0.04)', color: theme.inkMuted }} />
                  ))}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  },
};

const table: SectionDefinition = {
  type: 'table',
  label: 'Table',
  defaultTitle: 'Details',
  defaultMinLevel: 'business',
  chrome: 'card',
  render: ({ data, theme }) => {
    const columns: Array<{ key: string; label: string; align?: 'left' | 'right' | 'center' }> =
      (data as any)?.columns ?? [];
    const rows: Array<Record<string, any>> = (data as any)?.rows ?? [];
    if (rows.length === 0 || columns.length === 0) return null;
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align ?? 'left'} sx={{ color: theme.ink, borderColor: theme.grid }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, ri) => (
              <TableRow key={ri}>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align ?? 'left'} sx={{ color: theme.inkMuted, borderColor: theme.grid }}>
                    {String(row[c.key] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
};

const chipGroups: SectionDefinition = {
  type: 'chipGroups',
  label: 'Chip groups',
  defaultTitle: 'Inventory',
  defaultMinLevel: 'business',
  chrome: 'card',
  render: ({ data, theme }) => {
    const groups: Array<{ label: string; items: Array<{ label: string; warn?: boolean }> }> =
      (data as any)?.groups ?? [];
    if (groups.length === 0) return null;
    return (
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
        {groups.map((g) => (
          <Box key={g.label}>
            <Typography variant="subtitle2" sx={{ color: theme.ink, mb: 0.5 }}>
              {g.label}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
              {g.items.map((it) => (
                <Chip
                  key={it.label}
                  size="small"
                  label={it.label}
                  variant="outlined"
                  color={it.warn ? 'warning' : 'default'}
                  sx={{ borderColor: it.warn ? undefined : theme.grid, color: it.warn ? undefined : theme.inkMuted }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  },
};

const accordionList: SectionDefinition = {
  type: 'accordionList',
  label: 'Accordion list',
  defaultTitle: 'Reference',
  defaultMinLevel: 'technical',
  chrome: 'card',
  render: ({ data, theme }) => {
    const items: Array<{ title: string; subtitle?: string; body: string }> = (data as any)?.items ?? [];
    if (items.length === 0) return null;
    return (
      <>
        {items.map((it, i) => (
          <Accordion key={i} disableGutters sx={{ bgcolor: 'transparent', boxShadow: 'none', borderBottom: `1px solid ${theme.grid}` }}>
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: theme.inkMuted }} />}>
              <Typography variant="body2" sx={{ color: theme.ink }}>
                {it.title}
                {it.subtitle && <Box component="span" sx={{ color: theme.inkMuted }}> · {it.subtitle}</Box>}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ color: theme.inkMuted }}>
                {it.body}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </>
    );
  },
};

const cardGrid: SectionDefinition = {
  type: 'cardGrid',
  label: 'Card grid',
  defaultTitle: 'Breakdown',
  defaultMinLevel: 'technical',
  chrome: 'card',
  render: ({ data, theme }) => {
    const items: Array<{
      title: string;
      badge?: string;
      badgeColorBy?: 'severity';
      subtitle?: string;
      lines?: string[];
    }> = (data as any)?.items ?? [];
    if (items.length === 0) return null;
    return (
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
        {items.map((c, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 2, bgcolor: 'transparent', borderColor: theme.grid }}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1}>
              <Typography variant="subtitle2" sx={{ color: theme.ink }}>
                {c.title}
              </Typography>
              {c.badge && (
                <Chip
                  size="small"
                  label={c.badge}
                  sx={c.badgeColorBy === 'severity' ? { bgcolor: sevColor(c.badge.split('·').pop()?.trim()), color: '#fff' } : undefined}
                />
              )}
            </Box>
            {c.subtitle && (
              <Typography variant="caption" sx={{ color: theme.inkMuted, display: 'block' }}>
                {c.subtitle}
              </Typography>
            )}
            <Stack spacing={0.25} mt={c.subtitle ? 1 : 0.5}>
              {(c.lines ?? []).map((l, li) => (
                <Typography key={li} variant="body2" sx={{ color: theme.inkMuted }}>
                  {l}
                </Typography>
              ))}
            </Stack>
          </Paper>
        ))}
      </Box>
    );
  },
};

/**
 * A layered network diagram drawn from a topology model. Each tier is a
 * horizontal lane (cloud at top, ops at bottom); nodes lay out evenly within
 * their lane; edges are straight links coloured by kind.
 */
const ArchitectureDiagram: React.FC<{ model: TopoModel; theme: ReportTheme }> = ({ model, theme }) => {
  const NODE_W = 116;
  const NODE_H = 40;
  const BAND_H = 96;
  const LANE_LABEL_W = 96;

  const usedTiers = TOPO_TIERS.filter((t) => model.nodes.some((n) => n.tier === t.id));
  const maxPerTier = Math.max(1, ...usedTiers.map((t) => model.nodes.filter((n) => n.tier === t.id).length));
  const contentW = maxPerTier * (NODE_W + 24);
  const width = LANE_LABEL_W + contentW + 24;
  const height = usedTiers.length * BAND_H + 16;

  const pos = new Map<string, { x: number; y: number }>();
  usedTiers.forEach((tier, ti) => {
    const nodes = model.nodes
      .filter((n) => n.tier === tier.id)
      .sort((a, b) => a.row - b.row || a.col - b.col);
    const y = ti * BAND_H + BAND_H / 2 + 8;
    nodes.forEach((n, j) => {
      const x = LANE_LABEL_W + ((j + 1) / (nodes.length + 1)) * contentW;
      pos.set(n.id, { x, y });
    });
  });

  const nodeFill = theme.dark ? '#0f2038' : '#f2f6fb';
  const nodeText = theme.dark ? '#e6edf6' : '#1a2b45';
  const labelFill = theme.dark ? '#9fb3cc' : '#57606a';

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ minWidth: Math.min(width, 720) }}>
        {usedTiers.map((tier, ti) => (
          <g key={tier.id}>
            <line x1={0} y1={ti * BAND_H + 8} x2={width} y2={ti * BAND_H + 8} stroke={theme.grid} strokeDasharray="2 4" />
            <text x={4} y={ti * BAND_H + BAND_H / 2 + 12} fontSize={10} fill={labelFill}>
              {tier.short}
            </text>
          </g>
        ))}
        {model.edges.map((e) => {
          const a = pos.get(e.from);
          const b = pos.get(e.to);
          if (!a || !b) return null;
          const meta = TOPO_EDGE_KIND_META[e.kind];
          return (
            <line key={e.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={EDGE_COLOR[e.kind]} strokeWidth={1.5} strokeDasharray={meta.dash} opacity={meta.opacity} />
          );
        })}
        {model.nodes.map((n) => {
          const p = pos.get(n.id);
          if (!p) return null;
          const label = n.label.length > 16 ? `${n.label.slice(0, 15)}…` : n.label;
          return (
            <g key={n.id}>
              <rect x={p.x - NODE_W / 2} y={p.y - NODE_H / 2} width={NODE_W} height={NODE_H} rx={6} fill={nodeFill} stroke={n.legacy ? '#FFC000' : theme.accent} strokeWidth={n.legacy ? 2 : 1} />
              <text x={p.x} y={p.y + 4} fontSize={10} textAnchor="middle" fill={nodeText}>
                <title>{n.label}</title>
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

const architecture: SectionDefinition = {
  type: 'architecture',
  label: 'Architecture diagram',
  defaultTitle: 'Architecture',
  defaultMinLevel: 'business',
  defaultDataKey: 'architecture',
  chrome: 'card',
  render: ({ data, theme }) => {
    const model: TopoModel | null = (data as any)?.model ?? null;
    const summary = (data as any)?.summary as
      | { nodeCount: number; linkCount: number; legacyCount: number; edited?: boolean }
      | undefined;
    if (!model || model.nodes.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: theme.inkMuted }}>
          No inventory was captured, so no architecture diagram could be drafted.
        </Typography>
      );
    }
    return (
      <>
        <ArchitectureDiagram model={model} theme={theme} />
        {summary && (
          <Typography variant="caption" sx={{ color: theme.inkMuted, display: 'block', mt: 1 }}>
            {summary.nodeCount} components · {summary.linkCount} links
            {summary.legacyCount > 0 && ` · ${summary.legacyCount} end-of-life`}
            {summary.edited === false && ' · auto-drafted, unverified'}
          </Typography>
        )}
      </>
    );
  },
};

// --- Registry ---------------------------------------------------------------

export const SECTION_DEFINITIONS: SectionDefinition[] = [
  scoreHeader,
  narrative,
  threatBars,
  radar,
  heatmap,
  recommendations,
  table,
  chipGroups,
  architecture,
  accordionList,
  cardGrid,
];

export const SECTION_REGISTRY: Record<string, SectionDefinition> = Object.fromEntries(
  SECTION_DEFINITIONS.map((d) => [d.type, d])
);

export function getSectionDefinition(type: string): SectionDefinition | undefined {
  return SECTION_REGISTRY[type];
}

/**
 * One-line, plain-language description of what each section type renders, shown
 * in the report builder so an admin understands the section before committing
 * to it. Keyed by section `type`.
 */
export const SECTION_DESCRIPTIONS: Record<string, string> = {
  scoreHeader: 'A headline score with a label and a row of KPI stats. Best at the top of the report.',
  narrative: 'A block of prose plus optional bulleted groups — executive summary, scope, or methodology.',
  threatBars: 'A ranked horizontal bar chart of threats by calibrated risk score (0–100).',
  radar: 'A radar/spider chart comparing current maturity against the target across domains.',
  heatmap: 'A coloured matrix of threats (rows) against domains (columns); darker = more exposure.',
  recommendations: 'Ranked, role-aware recommendation cards with score impact, action steps, and tags.',
  table: 'A simple columns-and-rows table — e.g. product coverage or any tabular slice.',
  chipGroups: 'Grouped chips/labels — e.g. captured inventory, with end-of-life items flagged.',
  architecture: 'A layered network diagram drawn from the derived topology, with a summary caption.',
  accordionList: 'A collapsible list of titled entries — good for threat or control definitions.',
  cardGrid: 'A grid of small cards with a badge and a few lines each — e.g. per-domain breakdown.',
};

export function getSectionDescription(type: string): string | undefined {
  return SECTION_DESCRIPTIONS[type];
}
